import { Platform } from 'react-native';
import { api } from '../services/api';
import { cacheFileSink, fileSource, pickZipFiles } from '../zip/nativeFile';
import { pickZipBlobs } from '../zip/pickWeb';
import { extractEntry, type ExtractOptions } from '../zip/reader';
import { blobSink, blobSource, type ByteSource } from '../zip/source';
import type { ArchiveMedia } from './catalog';
import type { PreparedFile } from './importer';

// 9.18.10.4 · D-830 — the platform edges of the import. On the phone the
// archive is a file in Files and each entry becomes a temporary cache file;
// on the web the archive is the chosen Blob and each entry a Blob in memory.

/** The person's own ZIP files (all parts of one download), opened in place; empty when they cancel. */
export async function pickArchives(): Promise<ByteSource[]> {
  if (Platform.OS === 'web') return (await pickZipBlobs()).map(blobSource);
  return (await pickZipFiles()).map(fileSource);
}

type Extracted = { uri: string; dispose(): void; put(uploadUrl: string, contentType: string): Promise<number>; sizeBytes: number };

async function extract(source: ByteSource, item: ArchiveMedia, options: ExtractOptions): Promise<Extracted> {
  if (Platform.OS === 'web') {
    const blob = await extractEntry(source, item.entry, blobSink(item.contentType), options);
    const urlApi = (globalThis as { URL?: { createObjectURL?(b: Blob): string; revokeObjectURL?(u: string): void } }).URL;
    const uri = urlApi?.createObjectURL?.(blob) ?? '';
    return {
      uri,
      sizeBytes: blob.size,
      put: (uploadUrl, contentType) => api.uploadBlobBytes(blob, uploadUrl, contentType),
      dispose: () => { if (uri) urlApi?.revokeObjectURL?.(uri); },
    };
  }
  const file = await extractEntry(source, item.entry, cacheFileSink(item.extension), options);
  return {
    uri: file.uri,
    sizeBytes: file.size,
    put: (uploadUrl, contentType) => api.uploadMediaBytes(file.uri, uploadUrl, contentType),
    dispose: () => { try { file.delete(); } catch { /* the system may have cleared it */ } },
  };
}

/** A temporary copy ready for the upload pipeline; deleted by `dispose`. */
export function prepareForUpload(source: ByteSource, item: ArchiveMedia, signal: { readonly aborted: boolean }): Promise<PreparedFile> {
  return extract(source, item, { signal });
}

/** A local image for the review grid; deleted by `dispose`. */
export async function extractThumbnail(source: ByteSource, item: ArchiveMedia): Promise<{ uri: string; dispose(): void }> {
  const { uri, dispose } = await extract(source, item, {});
  return { uri, dispose };
}
